import React, { useState } from 'react';
import { Tabs, Tab, Box, useMediaQuery, useTheme, Chip, Fade } from '@mui/material';

interface CustomTabsProps {
  tabs: string[];
  tabContent: Record<string, React.ReactNode>;
  variant?: 'standard' | 'pills' | 'underline' | 'modern';
  showCount?: Record<string, number>; // Optional count badges for tabs
}

const CustomTabs: React.FC<CustomTabsProps> = ({ tabs, tabContent, variant = 'modern', showCount }) => {
  const [activeTab, setActiveTab] = useState(0);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery('(max-width:600px)');

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  // Variant styles
  const getTabStyles = (_index: number, isActive: boolean) => {
    const baseStyles = {
      fontFamily: 'Poppins',
      fontSize: '14px',
      fontWeight: isActive ? 600 : 500,
      textTransform: 'none' as const,
      minHeight: '44px',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      position: 'relative' as const,
    };

    switch (variant) {
      case 'pills':
        return {
          ...baseStyles,
          backgroundColor: isActive ? theme.palette.primary.main : 'rgba(0, 0, 0, 0.04)',
          color: isActive ? '#fff' : '#606E80',
          borderRadius: '24px',
          margin: '0 4px',
          padding: '8px 24px',
          '&:hover': {
            backgroundColor: isActive ? theme.palette.primary.dark : 'rgba(0, 0, 0, 0.08)',
            // transform: 'translateY(-2px)',
            boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
          },
          '&.Mui-selected': {
            backgroundColor: theme.palette.primary.main,
            color: '#fff',
          },
        };

      case 'underline':
        return {
          ...baseStyles,
          backgroundColor: 'transparent',
          color: isActive ? theme.palette.primary.main : '#606E80',
          borderBottom: 'none',
          margin: '0 8px',
          padding: '12px 20px',
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: '50%',
            transform: `translateX(-50%) scaleX(${isActive ? 1 : 0})`,
            width: '100%',
            height: '3px',
            backgroundColor: theme.palette.primary.main,
            borderRadius: '3px 3px 0 0',
            transition: 'transform 0.3s ease',
          },
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
            '&::after': {
              // transform: 'translateX(-50%) scaleX(1)',
              opacity: isActive ? 1 : 0.3,
            },
          },
          '&.Mui-selected': {
            color: theme.palette.primary.main,
          },
        };

      case 'modern':
        return {
          ...baseStyles,
          backgroundColor: isActive ? theme.palette.primary.main : 'transparent',
          color: isActive ? '#fff' : '#606E80',
          borderRadius: '12px',
          margin: '0 4px',
          padding: '10px 24px',
          border: isActive ? 'none' : '1px solid #E0E0E0',
          boxShadow: isActive ? '0 4px 12px rgba(25, 118, 210, 0.3)' : 'none',
          '&:hover': {
            backgroundColor: isActive ? theme.palette.primary.dark : 'rgba(25, 118, 210, 0.08)',
            // transform: 'translateY(-2px)',
            borderColor: isActive ? 'transparent' : theme.palette.primary.main,
          },
          '&.Mui-selected': {
            backgroundColor: theme.palette.primary.main,
            color: '#fff',
          },
        };

      default: // standard
        return {
          ...baseStyles,
          backgroundColor: isActive ? theme.palette.primary.main : '#F3F3F3',
          color: isActive ? '#fff' : '#181D25',
          borderRadius: isActive ? '10px' : '0',
          marginBottom: isSmallScreen ? '10px' : '0',
          padding: '12px 24px',
          '&:hover': {
            backgroundColor: isActive ? theme.palette.primary.dark : '#E8E8E8',
          },
          '&.Mui-selected': {
            backgroundColor: theme.palette.primary.main,
            color: '#fff',
          },
        };
    }
  };

  const getTabsContainerStyles = () => {
    const baseStyles = {
      maxWidth: isSmallScreen ? '100%' : 'fit-content',
      width: isSmallScreen ? '100%' : 'auto',
      flexDirection: isSmallScreen ? ('column' as const) : ('row' as const),
      minHeight: 'auto',
    };

    switch (variant) {
      case 'pills':
      case 'modern':
        return {
          ...baseStyles,
          backgroundColor: 'transparent',
          '& .MuiTabs-indicator': {
            display: 'none',
          },
          '& .MuiTabs-flexContainer': {
            gap: '8px',
          },
        };

      case 'underline':
        return {
          ...baseStyles,
          backgroundColor: 'transparent',
          borderBottom: '1px solid #E0E0E0',
          '& .MuiTabs-indicator': {
            display: 'none',
          },
          '& .MuiTabs-flexContainer': {
            gap: '4px',
          },
        };

      default:
        return {
          ...baseStyles,
          backgroundColor: '#F3F3F3',
          borderRadius: '10px',
          padding: '4px',
          '& .MuiTabs-indicator': {
            display: 'none',
          },
        };
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {/* Tabs Navigation */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={getTabsContainerStyles()}
          variant={isSmallScreen ? 'fullWidth' : 'standard'}
        >
          {tabs.map((tab, index) => (
            <Tab
              key={index}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span>{tab}</span>
                  {showCount && showCount[tab] !== undefined && (
                    <Chip
                      label={showCount[tab]}
                      size="small"
                      sx={{
                        height: '20px',
                        fontSize: '11px',
                        fontWeight: 600,
                        bgcolor: activeTab === index ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)',
                        color: activeTab === index ? '#fff' : '#606E80',
                        transition: 'all 0.3s',
                      }}
                    />
                  )}
                </Box>
              }
              sx={getTabStyles(index, activeTab === index)}
            />
          ))}
        </Tabs>

        {/* Optional: Tab counter summary */}
        {showCount && (
          <Chip
            label={`${Object.values(showCount).reduce((a, b) => a + b, 0)} Total`}
            size="small"
            sx={{
              bgcolor: '#E3F2FD',
              color: '#1976D2',
              fontWeight: 600,
              fontSize: '12px',
            }}
          />
        )}
      </Box>

      {/* Tab Content with Fade Animation */}
      <Fade in={true} timeout={300} key={activeTab}>
        <Box
          sx={{
            border: 'none',
            minHeight: '200px',
          }}
        >
          {tabContent[tabs[activeTab]]}
        </Box>
      </Fade>
    </Box>
  );
};

export default CustomTabs;
